import { Injectable } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';

type DismissalType = 'answered' | 'declined' | 'remind-later';

interface DismissalRecord {
  type: DismissalType;
  timestamp: number; // epoch ms
}

@Injectable({
  providedIn: 'root',
  deps: [DialogService],
})
export class FeedbackDialogService {
  private readonly STORAGE_KEY_PREFIX = 'genuz-satisfaction-survey';

  shouldShowSurvey(userId: string): boolean {
    const record = this.getRecord(userId);

    if (!record) {
      return true;
    }

    if (record.type === 'answered' || record.type === 'declined') {
      return false;
    }

    return !this.isSameCalendarDay(record.timestamp, Date.now());
  }

  private isSameCalendarDay(timestampA: number, timestampB: number): boolean {
    const dateA = new Date(timestampA);
    const dateB = new Date(timestampB);

    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  }

  setPermanentDismissal(userId: string, type: Extract<DismissalType, 'answered' | 'declined'>): void {
    this.saveRecord(userId, { type, timestamp: Date.now() });
  }

  setRemindLater(userId: string): void {
    this.saveRecord(userId, { type: 'remind-later', timestamp: Date.now() });
  }

  private getRecord(userId: string): DismissalRecord | null {
    try {
      const raw = localStorage.getItem(this.buildKey(userId));
      return raw ? (JSON.parse(raw) as DismissalRecord) : null;
    } catch {
      return null;
    }
  }

  private saveRecord(userId: string, record: DismissalRecord): void {
    try {
      localStorage.setItem(this.buildKey(userId), JSON.stringify(record));
    } catch {
      // silent failure - should not block the application
    }
  }

  private buildKey(userId: string): string {
    return `${this.STORAGE_KEY_PREFIX}:${userId}`;
  }
}
